import React from 'react';
import {
  Autocomplete,
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  ServiceInventoryPartOption,
  ServicePartThumbnail,
  serviceInventoryPartSearchText,
} from './ServiceInventoryPartOption';

const inventoryPartId = (part) => Number(part?.representative_id || part?.id);

const inventoryPartLabel = (part) => [
  part?.part_type || part?.description || 'Part',
  part?.sku || part?.biomed_part_number_sku || part?.part_number_oem,
  part?.location,
].filter(Boolean).join(' • ');

export default function ServiceLogInventoryEditPicker({
  inventoryParts = [],
  selectedParts = [],
  quantities = {},
  onSelectionChange,
  onQuantityChange,
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fbff', borderColor: '#cfe0ff' }}>
      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 900, color: '#001f3f', mb: 0.5 }}>
        <AddCircleOutlineIcon fontSize="small" /> Add or Replace Inventory Parts
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Choose additional or replacement parts. To replace a part, mark the old part for return above and select the new part here.
      </Typography>
      <Autocomplete
        multiple
        fullWidth
        size="small"
        options={inventoryParts}
        value={selectedParts}
        autoHighlight
        openOnFocus
        disableCloseOnSelect
        getOptionLabel={inventoryPartLabel}
        isOptionEqualToValue={(option, value) => inventoryPartId(option) === inventoryPartId(value)}
        filterOptions={(options, state) => {
          const tokens = String(state.inputValue || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
          if (!tokens.length) return options;
          return options.filter((part) => tokens.every((token) => serviceInventoryPartSearchText(part).includes(token)));
        }}
        onChange={(_event, parts) => onSelectionChange?.(parts)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search inventory parts to add"
            placeholder="Type SKU, OEM, serial, type, location..."
            helperText={`${inventoryParts.length.toLocaleString()} available • ${selectedParts.length} selected to add`}
          />
        )}
        renderOption={(props, part, { selected }) => {
          const id = inventoryPartId(part);
          return <ServiceInventoryPartOption optionProps={{ ...props, key: id }} part={part} selected={selected} label={inventoryPartLabel(part)} />;
        }}
      />
      {selectedParts.length > 0 && (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          {selectedParts.map((part) => {
            const id = inventoryPartId(part);
            const available = Math.max(1, Number(part.quantity ?? 1));
            return (
              <Grid item xs={12} md={6} key={id}>
                <Paper variant="outlined" sx={{ p: 1.25, display: 'flex', gap: 1.25, alignItems: 'center', borderRadius: 2, bgcolor: 'white' }}>
                  <ServicePartThumbnail part={part} size={58} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={`Quantity to add: ${part.part_type || part.sku || `Part #${id}`}`}
                      value={quantities[id] || 1}
                      inputProps={{ min: 1, max: available }}
                      helperText={`Available: ${available.toLocaleString()} • ${part.location || part.source_locations?.join?.(', ') || 'Location unavailable'}`}
                      onChange={(event) => {
                        const value = Math.max(1, Math.min(available, Number(event.target.value || 1)));
                        onQuantityChange?.(id, value);
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );
}
